import axiosServices from "../utils/axios";

import type {
  ResolveSystemPromptParams,
  SystemPrompt,
  SystemPromptMatchParams,
  SystemPromptPayload,
} from "../utils/types/systemPrompts";

const normalizeSystemPrompt = (item: any): SystemPrompt => {
  const systemPromptId = item?.system_prompt_id ?? item?.systemPromptId ?? item?.id;

  return {
    ...item,
    id: systemPromptId,
    system_prompt_id: systemPromptId,
    company_id: item?.company_id ?? item?.companyId ?? null,
    person_role: item?.person_role ?? item?.personRole ?? null,
    company_type: item?.company_type ?? item?.companyType ?? null,
    tag_label:
      item?.tag_label ??
      item?.tagLabel ??
      item?.tag_pick_list?.value ??
      item?.tagPickList?.value ??
      null,
    created_dt: item?.created_dt ?? item?.createdDt ?? null,
    created_by: item?.created_by ?? item?.createdBy ?? null,
    modified_dt: item?.modified_dt ?? item?.modifiedDt ?? null,
    modified_by: item?.modified_by ?? item?.modifiedBy ?? null,
    active: item?.active ?? true,
  };
};

const extractList = (responseData: any): any[] => {
  const data = responseData?.data ?? responseData;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;

  return [];
};

export const fetchSystemPromptsList = async (params?: {
  active?: boolean;
  q?: string;
  tag?: string;
}) => {
  try {
    const response = await axiosServices.get("/system-prompts", { params });
    return extractList(response.data).map(normalizeSystemPrompt);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to fetch system prompts",
    );
  }
};

const valuesMatch = (promptValue?: string | null, queryValue?: string | null) =>
  !!promptValue &&
  !!queryValue &&
  promptValue.trim().toLowerCase() === queryValue.trim().toLowerCase();

const hasScopedValue = (value?: string | null) =>
  !!String(value ?? "").trim();

const scopeMatches = (
  promptValue?: string | null,
  queryValue?: string | null,
) => !hasScopedValue(promptValue) || valuesMatch(promptValue, queryValue);

const normalizeCategory = (value?: string | null) =>
  String(value ?? "").trim().toLowerCase();

const tagMatchesEmailCategories = (
  prompt: SystemPrompt,
  params: SystemPromptMatchParams,
) => {
  if (!prompt.tag) return true;

  const emailCategories = new Set(
    (params.email_categories ?? []).map(normalizeCategory).filter(Boolean),
  );
  const tagLabel = normalizeCategory(prompt.tag_label);

  return !!tagLabel && emailCategories.has(tagLabel);
};

const getPromptSpecificity = (
  prompt: SystemPrompt,
  params: SystemPromptMatchParams,
) =>
  (prompt.company_id && prompt.company_id === params.company_id ? 8 : 0) +
  (valuesMatch(prompt.person_role, params.person_role) ? 4 : 0) +
  (valuesMatch(prompt.company_type, params.company_type) ? 2 : 0) +
  (prompt.tag ? 1 : 0);

const sortPromptMatches = (
  prompts: SystemPrompt[],
  params: SystemPromptMatchParams,
) =>
  [...prompts].sort((a, b) => {
    const specificity = getPromptSpecificity(b, params) - getPromptSpecificity(a, params);
    if (specificity !== 0) return specificity;

    const bDate = new Date(b.modified_dt ?? b.created_dt ?? 0).getTime();
    const aDate = new Date(a.modified_dt ?? a.created_dt ?? 0).getTime();
    return bDate - aDate;
  });

const findPromptMatches = (
  prompts: SystemPrompt[],
  params: SystemPromptMatchParams,
) => {
  const activePrompts = prompts.filter((prompt) =>
    prompt.active &&
    tagMatchesEmailCategories(prompt, params) &&
    scopeMatches(prompt.company_id, params.company_id) &&
    scopeMatches(prompt.person_role, params.person_role) &&
    scopeMatches(prompt.company_type, params.company_type),
  );

  return sortPromptMatches(activePrompts, params);
};

const resolveSystemPromptsFromList = async (params: SystemPromptMatchParams) => {
  const prompts = await fetchSystemPromptsList({ active: true });

  return findPromptMatches(prompts, params);
};

export const addSystemPrompt = async (data: SystemPromptPayload) => {
  try {
    const response = await axiosServices.post("/system-prompts", data);
    return normalizeSystemPrompt(response.data?.data);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to add the new system prompt",
    );
  }
};

export const updateSystemPrompt = async (
  id: string,
  data: SystemPromptPayload,
) => {
  try {
    const response = await axiosServices.put(`/system-prompts/${id}`, data);
    return normalizeSystemPrompt(response.data?.data ?? { ...data, id });
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to update system prompt",
    );
  }
};

export const deleteSystemPrompt = async (id: string) => {
  try {
    const response = await axiosServices.delete(`/system-prompts/${id}`, {
      data: {
        forceDelete: true,
      },
      params: {
        forceDelete: true,
      },
    });
    if (response?.data && response.data.success === false) {
      throw new Error(response.data.message || "Failed to delete system prompt");
    }
    return { success: response?.data?.success, id };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to delete system prompt",
    );
  }
};

const cleanPromptMatchParams = (
  params: SystemPromptMatchParams | ResolveSystemPromptParams,
) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== "";
    }),
  );

export const fetchMatchingSystemPrompts = async (
  params: SystemPromptMatchParams,
) => {
  const cleanParams = Object.fromEntries(
    Object.entries(cleanPromptMatchParams(params)).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(",") : value,
    ]),
  );

  try {
    const response = await axiosServices.get("/system-prompts/matches", {
      params: cleanParams,
    });
    const prompts = extractList(response.data).map(normalizeSystemPrompt);

    return prompts.length ? prompts : resolveSystemPromptsFromList(params);
  } catch (error) {
    console.error("System prompt matcher failed, falling back to list:", error);
    return resolveSystemPromptsFromList(params);
  }
};

/**
 * @deprecated Use fetchMatchingSystemPrompts() directly for reply prompt UI.
 * This legacy wrapper returns only the first matching prompt.
 */
export const resolveSystemPrompt = async (
  params: ResolveSystemPromptParams,
) => {
  try {
    const response = await axiosServices.get("/system-prompts/resolve", {
      params: cleanPromptMatchParams(params),
    });
    const prompt = response.data?.data;

    return prompt
      ? normalizeSystemPrompt(prompt)
      : (await fetchMatchingSystemPrompts(params))[0] ?? null;
  } catch (error) {
    console.error("System prompt resolver failed, falling back to list:", error);
    return (await fetchMatchingSystemPrompts(params))[0] ?? null;
  }
};
